import { Dialog, useMediaQuery, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useNotification } from "app/NotificationProvider";
import { AnalyticsEvents, mixpanel } from "common/analytics";
import { useTranslation } from "common/locales";
import { queryKeys } from "common/queryKeys";
import EnterReseedEvents from "features/bulkReseedEventInput/EnterReseedEvents";
import EnterFeedEvent from "features/feedPurchaseDetail/EnterFeedEvent";
import EnterFeedPurchaseEvent from "features/feedPurchaseDetail/EnterFeedPurchaseEvent";
import { checkConsistency } from "features/feedPurchaseDetail/feedPurchaseValidation";
import EnterFertilizerEvent from "features/fertilizerInput/EnterFertilizerEvent";
import {
  convertFromFormInput,
  FertilizerEventFormData,
} from "features/fertilizerInput/FertilizerEventFormData";
import EnterFieldPreparationEvent from "features/fieldPreparationInput/EnterFieldPreparationEvent";
import {
  convertFromFormInput as convertFromFieldPrepFormInput,
  FieldPreparationEventFormData,
} from "features/fieldPreparationInput/FieldPreparationEventFormData";
import { NameAndGuid } from "features/general/NameAndGuidMultiSelectDropdown";
import { StyledInputContainer } from "features/StyledInputContainer";
import EnterYieldEvent from "features/yieldInput/EnterYieldEvent";
import {
  GENERAL_FEED_TYPE,
  GENERAL_FEED_UNITS,
  GeneralFeedEvent,
  GRASS_TYPE,
  MACHINERY,
  PURCHASE_CURRENCY,
  RESEED_TOTAL_AMOUNT_UNIT,
  RESEED_TYPE,
  SEEDING_METHOD,
  useCreateFeedEvent,
  useCreateFeedPurchaseEvent,
  useCreateFertilizerEvent,
  useCreateFieldPreparationEvent,
  useCreateReseedEventForFarm,
  useCreateYieldEvent,
  YieldEvent,
} from "model";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppStore } from "stores";

export function AddReseedEventDialog({
  dialogOpen,
  setDialogOpen,
}: {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useNotification();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const createReseedEventMutation = useCreateReseedEventForFarm();

  // Use Zustand for current farm ID
  const currentFarmId = useAppStore((state) => state.currentFarmId);

  const handleClose = () => {
    setDialogOpen(false);
    mixpanel.track(AnalyticsEvents.RESEED_EVENT_CREATE_CANCELLED);
  };

  const [newGrassType, setNewGrassType] = useState<GRASS_TYPE | undefined>(
    undefined
  );
  const [grassTypeError, setGrassTypeError] = useState<boolean>(false);
  const [seedingMethod, setSeedingMethod] = useState<
    SEEDING_METHOD | undefined
  >(undefined);
  const [seedingMethodError, setSeedingMethodError] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>("");
  const [selectedFields, setSelectedFields] = useState<NameAndGuid[]>([]);
  const [reseedDate, setReseedDate] = useState<Date | null>(new Date());
  const [reseedDateError, setReseedDateError] = useState<boolean>(false);
  const [machinery, setMachinery] = useState<MACHINERY[]>([]);
  const [fuel, setFuel] = useState<number>("" as unknown as number);
  const [operationalCost, setOperationalCost] = useState<number>(
    "" as unknown as number
  );
  const [productCost, setProductCost] = useState<number>(
    "" as unknown as number
  );
  const [reseedType, setReseedType] = useState<RESEED_TYPE>(RESEED_TYPE.RESEED);
  const [seedingRate, setSeedingRate] = useState<number>(
    "" as unknown as number
  );
  const [productName, setProductName] = useState<string>("");
  const [totalAmountUnit, setTotalAmountUnit] =
    useState<RESEED_TOTAL_AMOUNT_UNIT>(
      "" as unknown as RESEED_TOTAL_AMOUNT_UNIT
    );
  const [totalAmount, setTotalAmount] = useState<number>(
    "" as unknown as number
  );
  const [fieldsSelectInputError, setFieldsSelectInputError] =
    useState<boolean>(false);

  async function handleSave() {
    if (!newGrassType) {
      setGrassTypeError(true);
    }
    if (!reseedDate) {
      setReseedDateError(true);
    }
    if (selectedFields.length === 0) {
      setFieldsSelectInputError(true);
    }
    if (!seedingMethod) {
      setSeedingMethodError(true);
    }

    if (
      !newGrassType ||
      !reseedDate ||
      !seedingMethod ||
      selectedFields.length === 0 ||
      grassTypeError ||
      reseedDateError ||
      fieldsSelectInputError ||
      seedingMethodError
    ) {
      return;
    }

    const fieldGuids = selectedFields.map(
      (selectedField: NameAndGuid) => selectedField.guid ?? ""
    );

    const totalAmountUnitForPost =
      totalAmountUnit === ("" as unknown as RESEED_TOTAL_AMOUNT_UNIT)
        ? undefined
        : totalAmountUnit;

    const finalOperationalCost =
      operationalCost === ("" as unknown as number)
        ? undefined
        : operationalCost;

    const finalProductCost =
      productCost === ("" as unknown as number) ? undefined : productCost;

    await createReseedEventMutation.mutateAsync({
      reseedEvent: {
        note: notes,
        reseedDate: reseedDate.toISOString(),
        newGrassType: newGrassType,
        seedingMethod: seedingMethod,
        machinery: machinery,
        fuel: fuel,
        operationalCost: finalOperationalCost,
        productCost: finalProductCost,
        operationalCostUnit: finalOperationalCost
          ? PURCHASE_CURRENCY.GBP
          : undefined,
        productCostUnit: finalProductCost ? PURCHASE_CURRENCY.GBP : undefined,
        fieldGuids,
        reseedType: reseedType,
        productName: productName,
        seedingRate: seedingRate,
        totalAmount: totalAmount ? totalAmount : undefined,
        totalAmountUnit: totalAmount ? totalAmountUnitForPost : undefined,
      },
      farmGuid: currentFarmId ?? "",
    });
    // Invalidate field queries for affected fields
    await Promise.all(
      fieldGuids.map((fieldGuid) =>
        queryClient.invalidateQueries({
          queryKey: queryKeys.fields.detail(fieldGuid),
        })
      )
    );
    notify(t("updateGrassTypes.success"));
    mixpanel.track(AnalyticsEvents.RESEED_EVENT_CREATED);
    setDialogOpen(false);
    navigate("/records");
  }

  return (
    <Dialog
      fullScreen={isMobile}
      maxWidth="md"
      disableEscapeKeyDown
      open={dialogOpen}
      onClose={handleClose}
    >
      <StyledInputContainer isMobile={isMobile}>
        <EnterReseedEvents
          newGrassType={newGrassType}
          selectedFields={selectedFields}
          setSelectedFields={setSelectedFields}
          setNewGrassType={setNewGrassType}
          handleSave={handleSave}
          setNotes={setNotes}
          notes={notes}
          reseedDate={reseedDate}
          setReseedDate={setReseedDate}
          grassTypeError={grassTypeError}
          reseedDateError={reseedDateError}
          setGrassTypeError={setGrassTypeError}
          setSeedingMethod={setSeedingMethod}
          seedingMethodError={seedingMethodError}
          setSeedingMethodError={setSeedingMethodError}
          setReseedDateError={setReseedDateError}
          fieldsSelectInputError={fieldsSelectInputError}
          setFieldsSelectInputError={setFieldsSelectInputError}
          seedingMethod={seedingMethod}
          machinery={machinery}
          setMachinery={setMachinery}
          fuel={fuel}
          setFuel={setFuel}
          enableFieldSelection={true}
          onCancel={handleClose}
          reseedType={reseedType}
          setReseedType={setReseedType}
          productName={productName}
          setProductName={setProductName}
          seedingRate={seedingRate}
          setSeedingRate={setSeedingRate}
          totalAmountUnit={totalAmountUnit}
          setTotalAmountUnit={setTotalAmountUnit}
          totalAmount={totalAmount}
          setTotalAmount={setTotalAmount}
          operationalCost={operationalCost}
          setOperationalCost={setOperationalCost}
          productCost={productCost}
          setProductCost={setProductCost}
        />
      </StyledInputContainer>
    </Dialog>
  );
}

export function AddFeedEventDialog({
  dialogOpen,
  setDialogOpen,
}: {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}) {
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  const { notify } = useNotification();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const createFeedEventMutation = useCreateFeedEvent();

  const handleClose = () => {
    setDialogOpen(false);
    mixpanel.track(AnalyticsEvents.FEED_EVENT_CREATE_CANCELLED);
  };

  async function handleSave(generalFeedEvent: GeneralFeedEvent) {
    await createFeedEventMutation.mutateAsync({
      generalFeedEvent,
      farmGuid: currentFarmId ?? "",
    });
    notify(t("addFeedEvent.add.success"));
    mixpanel.track(AnalyticsEvents.FEED_EVENT_CREATED);
    setDialogOpen(false);
    navigate("/records");
  }

  return (
    <Dialog
      fullScreen={isMobile}
      maxWidth="md"
      disableEscapeKeyDown
      open={dialogOpen}
      onClose={handleClose}
    >
      <StyledInputContainer isMobile={isMobile}>
        <EnterFeedEvent
          feedEvent={undefined}
          handleSave={handleSave}
          handleClose={handleClose}
        />
      </StyledInputContainer>
    </Dialog>
  );
}

export function AddFeedPurchaseEventDialog({
  dialogOpen,
  setDialogOpen,
}: {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const createFeedPurchaseMutation = useCreateFeedPurchaseEvent();

  // Use Zustand for current farm ID
  const currentFarmId = useAppStore((state) => state.currentFarmId);

  const handleClose = () => {
    setDialogOpen(false);
    mixpanel.track(AnalyticsEvents.FEED_PURCHASE_CREATE_CANCELLED);
  };

  const [feedType, setFeedType] = useState<GENERAL_FEED_TYPE | undefined>(
    undefined
  );
  const [feedTypeError, setFeedTypeError] = useState<boolean>(false);
  const [feedUnit, setFeedUnit] = useState<GENERAL_FEED_UNITS | undefined>(
    undefined
  );
  const [feedUnitError, setFeedUnitError] = useState<boolean>(false);
  const [notes, setNotes] = useState<string | undefined>(undefined);
  const [cost, setCost] = useState<string | undefined>();
  const [costError, setCostError] = useState<boolean>(false);
  const [feedAmount, setFeedAmount] = useState<string | undefined>();
  const [feedAmountError, setFeedAmountError] = useState<boolean>(false);
  const [date, setDate] = useState<Date | null>(new Date());
  const [dateError, setDateError] = useState<boolean>(false);

  async function createfeedPurchaseEvents() {
    if (!feedType || !date || !currentFarmId || !feedAmount) {
      return;
    }

    return await createFeedPurchaseMutation.mutateAsync({
      farmGuid: currentFarmId,
      guid: " ",
      note: notes ?? "",
      feedPurchaseEventDate: date.toISOString(),
      feedPurchases: [
        {
          costCurrency: cost === undefined ? undefined : PURCHASE_CURRENCY.GBP,
          cost: cost ? Number(cost) : undefined,
          unitType: feedUnit ?? GENERAL_FEED_UNITS.TONNE,
          feedType: feedType,
          amount: Number(feedAmount),
        },
      ],
    });
  }

  async function handleSave() {
    const consistent = checkConsistency(
      feedType,
      setFeedTypeError,
      feedUnit,
      setFeedUnitError,
      date,
      setDateError,
      dateError,
      feedAmount,
      setFeedAmountError,
      cost,
      setCostError
    );
    if (!consistent) {
      return;
    }
    await createfeedPurchaseEvents();

    notify(t("createfeedPurchaseEvent.success"));
    mixpanel.track(AnalyticsEvents.FEED_PURCHASE_CREATED);
    setDialogOpen(false);
    navigate("/records");
  }

  return (
    <Dialog
      fullScreen={isMobile}
      maxWidth="md"
      disableEscapeKeyDown
      open={dialogOpen}
      onClose={handleClose}
    >
      <StyledInputContainer isMobile={isMobile}>
        <EnterFeedPurchaseEvent
          feedType={feedType}
          cost={cost}
          setCost={setCost}
          setFeedType={setFeedType}
          handleSave={handleSave}
          setNotes={setNotes}
          note={notes}
          date={date}
          setDate={setDate}
          feedTypeError={feedTypeError}
          dateError={dateError}
          setFeedTypeError={setFeedTypeError}
          setFeedUnit={setFeedUnit}
          feedUnitError={feedUnitError}
          setFeedUnitError={setFeedUnitError}
          feedUnit={feedUnit}
          feedAmount={feedAmount}
          setFeedAmount={setFeedAmount}
          feedAmountError={feedAmountError}
          setFeedAmountError={setFeedAmountError}
          costError={costError}
          setCostError={setCostError}
          handleClose={handleClose}
        />
      </StyledInputContainer>
    </Dialog>
  );
}

export function AddYieldEventDialog({
  dialogOpen,
  setDialogOpen,
}: {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}) {
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  const { notify } = useNotification();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const createYieldEventMutation = useCreateYieldEvent();

  const handleClose = () => {
    setDialogOpen(false);
    mixpanel.track(AnalyticsEvents.YIELD_EVENT_CREATE_CANCELLED);
  };

  async function handleSave(yieldEvent: YieldEvent) {
    await createYieldEventMutation.mutateAsync({
      yieldEvent,
      farmGuid: currentFarmId ?? "",
    });
    notify(t("addYieldEvent.add.success"));
    mixpanel.track(AnalyticsEvents.YIELD_EVENT_CREATED);
    setDialogOpen(false);
    navigate("/records");
  }

  return (
    <Dialog
      fullScreen={isMobile}
      maxWidth="md"
      disableEscapeKeyDown
      open={dialogOpen}
      onClose={handleClose}
    >
      <StyledInputContainer isMobile={isMobile}>
        <EnterYieldEvent
          yieldEvent={undefined}
          handleSave={handleSave}
          handleClose={handleClose}
        />
      </StyledInputContainer>
    </Dialog>
  );
}

export function AddFertilizerEventDialog({
  dialogOpen,
  setDialogOpen,
}: {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}) {
  const { notify } = useNotification();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const createFertilizerEventMutation = useCreateFertilizerEvent();

  const handleClose = () => {
    setDialogOpen(false);
    mixpanel.track(AnalyticsEvents.FERTILIZER_EVENT_CREATE_CANCELLED);
  };

  async function handleSave(
    fertilizerEventFormData: FertilizerEventFormData,
    farmGuid: string | undefined
  ) {
    const fertilizerEventBody = convertFromFormInput(fertilizerEventFormData);
    farmGuid = farmGuid ?? "";
    await createFertilizerEventMutation.mutateAsync({
      fertilizerEvent: fertilizerEventBody,
      farmGuid,
    });

    notify(t("addFertilizerEvent.add.success"));
    mixpanel.track(AnalyticsEvents.FERTILIZER_EVENT_CREATED);
    setDialogOpen(false);
    navigate("/records");
  }

  return (
    <Dialog
      fullScreen={isMobile}
      maxWidth="md"
      disableEscapeKeyDown
      open={dialogOpen}
      onClose={handleClose}
    >
      <StyledInputContainer isMobile={isMobile}>
        <EnterFertilizerEvent
          fertilizerEvent={undefined}
          handleSave={handleSave}
          handleClose={handleClose}
          fieldSelectDisabled={false}
        />
      </StyledInputContainer>
    </Dialog>
  );
}

export function AddFieldPreparationEventDialog({
  dialogOpen,
  setDialogOpen,
}: {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}) {
  const { notify } = useNotification();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  const createFieldPreparationEventMutation = useCreateFieldPreparationEvent();

  const handleClose = () => {
    setDialogOpen(false);
    mixpanel.track(AnalyticsEvents.FIELD_PREPARATION_CREATE_CANCELLED);
  };

  async function handleSave(
    fieldPreparationEventFormData: FieldPreparationEventFormData
  ) {
    const fieldPreparationEvent = convertFromFieldPrepFormInput(
      fieldPreparationEventFormData
    );
    await createFieldPreparationEventMutation.mutateAsync({
      fieldPreparationEvent,
      farmGuid: currentFarmId ?? "",
    });

    notify(t("addFieldPreparationEvent.add.success"));
    mixpanel.track(AnalyticsEvents.FIELD_PREPARATION_CREATED);
    setDialogOpen(false);
    navigate("/records");
  }

  return (
    <Dialog
      fullScreen={isMobile}
      maxWidth="md"
      disableEscapeKeyDown
      open={dialogOpen}
      onClose={handleClose}
    >
      <StyledInputContainer isMobile={isMobile}>
        <EnterFieldPreparationEvent
          fieldPreparationEvent={undefined}
          handleSave={handleSave}
          handleClose={handleClose}
          fieldSelectDisabled={false}
        />
      </StyledInputContainer>
    </Dialog>
  );
}
